<script lang="ts">
    import { select } from '@rxsv/core';
    import { filter } from 'rxjs/operators';

    import Chart from './Chart.svelte';
    import AppError from './AppError.svelte';

    import type { FilterDataArray, FilterId, Setting } from 'portlet/models';
    import { AppAction } from 'portlet/store/index';
    import { Selectors } from 'portlet/store/index';
    import {
        getLoggerContext,
        getStoreContext,
        getStoreInterfaceContext,
        getServiceContext,
    } from 'portlet/common/contexts';

    import { Selectors as FilterSelectors } from 'portlet/store/filters-settings/index';
    import { Selectors as ViewSelectors } from 'portlet/store/view/index';
    import SettingsFieldGroup from './portletMenu/settings/SettingsFieldGroup.svelte';
    import type { ChartState } from '@bayer-int/portletce-aclibv2';
    import Spinner from './Spinner.svelte';
    import { InteractionToFilterService } from 'portlet/services';

    interface Props {
        nomandatorysettinglabel: boolean;
    }

    let { nomandatorysettinglabel }: Props = $props();

    const logger$ = getLoggerContext();
    const store = getStoreContext();
    const { triggersAndActions } = getStoreInterfaceContext();
    const service$ = getServiceContext();

    const isDataLoading$ = store.state$.pipe(select(Selectors.isDataLoadingSelector));
    const isDataReloading$ = store.state$.pipe(select(Selectors.isDataReloadingSelector));
    const isPortletSuspended$ = store.state$.pipe(select(ViewSelectors.isSuspendedSelector));
    const dataError$ = store.state$.pipe(select(Selectors.dataErrorSelector));
    const initError$ = store.state$.pipe(select(Selectors.initErrorSelector));
    const filterData$ = store.state$.pipe(select(Selectors.filterSelector));
    const isDataConsistent$ = store.state$.pipe(select(Selectors.isDataConsistent));
    const viewData$ = store.state$.pipe(
        filter(Selectors.isChartDataUpToDateSelector),
        filter(Selectors.areSettingsConsistentSelector),
        select(Selectors.chartConfigSelector),
    );
    const portletConfig$ = store.state$.pipe(select(Selectors.configSelector));
    const currentDimensionSetting$ = store.state$.pipe(
        select(FilterSelectors.currentDimensionSetting),
    );
    const currentHierarchySetting$ = store.state$.pipe(
        select(FilterSelectors.currentHierarchySetting),
    );
    const currentLinkedHierarchiesSetting$ = store.state$.pipe(
        select(FilterSelectors.currentLinkedHierarchiesSetting),
    );
    const currentApplyFilterOnExactDrillOnlySetting$ = store.state$.pipe(
        select(FilterSelectors.currentApplyFilterOnExactDrillOnlySetting),
    );
    const currentApplyFilterOnSelectionSetting$ = store.state$.pipe(
        select(FilterSelectors.currentApplyFilterOnSelectionSetting),
    );
    const currentApplyFilterToUpstreamHierarchyLevelsSetting$ = store.state$.pipe(
        select(FilterSelectors.currentApplyFilterToUpstreamHierarchyLevelsSetting),
    );
    const areMandatorySettingsInitialized$ = store.state$.pipe(
        select(FilterSelectors.areMandatorySettingsInitialized),
    );
    const nonDefaultMandatorySettingsFormDefinitions$ = store.state$.pipe(
        select(FilterSelectors.nonDefaultMandatorySettingsFormDefinitions),
    );
    const nonEmptyMandatorySettingsFormDefinitions$ = store.state$.pipe(
        select(FilterSelectors.nonEmptyMandatorySettingsFormDefinitions),
    );
    const resettableSettingsFormDefinitions$ = store.state$.pipe(
        select(FilterSelectors.resettableSettingsFormDefinitions),
    );
    const queryMetadata$ = store.state$.pipe(select(FilterSelectors.queryMetadataSelector));
    const listenTo$ = store.state$.pipe(select(FilterSelectors.listenToOnCurrentView));

    let seriesVisibility: null | unknown = $state(null);
    let logger = $derived($logger$);
    let service = $derived($service$);
    let isLoading = $derived($isDataLoading$ && !$isDataReloading$ && !$isPortletSuspended$);
    let error = $derived($dataError$ || $initError$);
    let filterDataArray = $derived($filterData$);
    let data = $derived($viewData$);
    let currentDimensionSetting = $derived($currentDimensionSetting$);
    let currentHierarchySetting = $derived($currentHierarchySetting$);
    let currentLinkedHierarchiesSetting = $derived($currentLinkedHierarchiesSetting$);
    let currentApplyFilterOnExactDrillOnlySetting = $derived(
        $currentApplyFilterOnExactDrillOnlySetting$,
    );
    let currentApplyFilterOnSelectionSetting = $derived($currentApplyFilterOnSelectionSetting$);
    let currentApplyFilterToUpstreamHierarchyLevelsSetting = $derived(
        $currentApplyFilterToUpstreamHierarchyLevelsSetting$,
    );
    let isDataConsistent = $derived($isDataConsistent$);
    let mandatorySettingsInitialized = $derived($areMandatorySettingsInitialized$);
    let nonDefaultMandatorySettingsFormDefinitions = $derived(
        $nonDefaultMandatorySettingsFormDefinitions$,
    );
    let mandatorySettingsSelected = $derived(
        nonDefaultMandatorySettingsFormDefinitions.every(setting => setting.value !== null),
    );
    let nonEmptyMandatorySettingsFormDefinitions = $derived(
        $nonEmptyMandatorySettingsFormDefinitions$,
    );
    let resettableSettingsFormDefinitions = $derived($resettableSettingsFormDefinitions$);
    let queryMetadata = $derived($queryMetadata$);
    let listenTo = $derived($listenTo$);
    let filterKeyToEmitFrom = $derived($viewData$?.filterKeyToEmitFrom || null);
    let portletUniqueId = $derived($portletConfig$?.uniqueid);
    let viewId = $derived($viewData$?.viewId);

    $effect(() => {
        seriesVisibility =
            (portletUniqueId &&
                viewId &&
                service.sessionStorageService.getSeriesVisibility(portletUniqueId, viewId)) ||
            null;
    });
    // We cannot watch for changes in session storage, so we need to trigger it manually
    $effect(() => {
        if ($isDataReloading$) {
            updateSeriesVisibility();
        }
    });

    const updateSeriesVisibility = () => {
        seriesVisibility =
            (portletUniqueId &&
                viewId &&
                service.sessionStorageService.getSeriesVisibility(portletUniqueId, viewId)) ||
            null;
    };
    const dispatchFilters = (filter: FilterDataArray) => {
        if (!currentApplyFilterOnSelectionSetting)
            return logger.warn(
                'Filters ignored due to Apply Filters On Selection setting:',
                filter,
            );

        return store.action$.next(AppAction.filters.CHANGE_FILTER_VALUES(filter));
    };

    const dispatchClearFilterRequest = (items: FilterId[]) => {
        if (!currentApplyFilterOnSelectionSetting)
            return logger.warn(
                'Filter clearing ignored due to Apply Filters On Selection setting:',
                items,
            );

        return store.action$.next(
            AppAction.filters.CLEAR_FILTERS_REQUEST(items.map(item => ({ item }))),
        );
    };

    const dispatchSettingChange = (setting: Setting) => {
        store.action$.next(AppAction.filters.CHANGE_SETTING_VALUE(setting));
    };

    const dispatchClearSettings = () => {
        const filterItemsToClear = InteractionToFilterService.matchListenToByKeywords(listenTo, [
            'clear',
        ]).map(({ item }) => item);

        // clearing filters when clearing resettable settings because in variant hierarchy selelector
        // we do not want to persist selections between different variants
        if (filterItemsToClear.length) dispatchClearFilterRequest(filterItemsToClear);

        store.action$.next(
            AppAction.filters.CHANGE_SETTING_VALUES(
                resettableSettingsFormDefinitions.map(setting => ({
                    key: setting.key,
                    value: null,
                })),
            ),
        );
    };

    const dispatchStateChangeEvent = (chartState: ChartState) => {
        store.action$.next(AppAction.portlet.PORTLET_VISUALIZATION_STATE_CHANGE(chartState));
    };

    const dispatchPerformanceDataTimeChangeEvent = (duration: number) => {
        store.action$.next(AppAction.performance.SAVE_PERFORMANCE_DATA_CALLS_TIME(duration));
    };
</script>

<main class="content">
    {#if isLoading}
        <div class="content__message">
            <Spinner />
        </div>
    {:else if error}
        <AppError {error} />
    {:else if !mandatorySettingsSelected}
        <SettingsFieldGroup
            settingDefinitions={mandatorySettingsInitialized
                ? resettableSettingsFormDefinitions
                : nonEmptyMandatorySettingsFormDefinitions}
            on:change={({ detail }) =>
                dispatchSettingChange({ key: detail.field.key, value: detail.field.value })}
            nolabel={nomandatorysettinglabel}
        />
    {:else if data}
        <Chart
            anychartConfig={data}
            uniqueId={portletUniqueId || ''}
            {dispatchFilters}
            {dispatchClearFilterRequest}
            {currentDimensionSetting}
            {currentHierarchySetting}
            {currentLinkedHierarchiesSetting}
            {currentApplyFilterOnExactDrillOnlySetting}
            {currentApplyFilterToUpstreamHierarchyLevelsSetting}
            {filterDataArray}
            {queryMetadata}
            {listenTo}
            {isDataConsistent}
            {filterKeyToEmitFrom}
            {seriesVisibility}
            {dispatchStateChangeEvent}
            {dispatchPerformanceDataTimeChangeEvent}
            requestDatatipContent={triggersAndActions.requestDatatipContent.bind(
                triggersAndActions,
            )}
            onDataTipContentClick={triggersAndActions.onDataTipNavigationItemClick.bind(
                triggersAndActions,
            )}
            on:chartlib-interaction
        />
        {#if resettableSettingsFormDefinitions.length > 0}
            <button
                title="restart"
                aria-label="restart"
                onclick={dispatchClearSettings}
                class="content__restart-button"
            >
                <i class="material-icons">{'restart_alt'}</i>
            </button>
        {/if}
    {/if}
</main>

<style lang="scss">
    @use '../../../common/styles/mixins.scss' as m;

    .content {
        position: relative;
        flex-grow: 1;
        max-height: 100%;
        overflow: hidden;
        padding: 1rem;
        padding-top: 0;

        display: flex;
        justify-content: center;

        &__message {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            max-width: 100%;
        }

        &__restart-button {
            @include m.reset-button;

            width: 32px;
            height: 32px;
            border-radius: 32px;

            display: flex;
            justify-content: center;
            align-items: center;

            padding: 8px;
            margin: 2px;

            cursor: pointer;

            color: #505050;
            background-color: #fafafa;

            &:hover {
                background-color: #eee;
            }
        }
    }
</style>
